import {
    ASDRatchets,
    AmuContractTerms,
    ContractDiscountRatesInput,
    ContractTermsInput,
    CoreRate,
    CoreRates,
    CustomDiscountRates,
    DiscountRatesInputs,
    ModifyCoreRatesInput,
    ModifyCoreRatesOutput,
    PlayCountAggregationTypeMappingList,
    PrsDeductionCap,
    PublishContractInput,
    PublishContractOutput,
    RateMultipliersInput,
    RateTier,
    Revenue,
    RoyaltyModel,
    RoyaltyModelType,
    RoyaltyModelTypesEnum,
    RoyaltyModelsEnum,
    SetContractTermsInput,
    SetContractTermsOutput,
    SetDiscountRatesInput,
    SetDiscountRatesOutput,
    StreamingTaxDeductionCaps,
    TerritoryOverrides,
    TerritoryTermInput,
    TerritoryTermInputs,
    ThirdPartyDeductionCaps,
    ThresholdOperatorEnum,
    TieredRates,
    VendorShareCalculationTypeMappingList,
    VpirBundleRevenueDeduction
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';

import { isEmpty, isUndefined } from 'lodash';
import { contractConfigurationClient } from 'src/api/client';
import { CalcType } from 'src/components/RateCardDetail/ThirdPartyRates/ThirdPartyRatesAutoCalcUtil';
import { RateCardCoreRateGridColumnName, RateMultiplierDisplayName } from 'src/constants';
import {
    CORE_PLAN_COLUMN_TO_CORE_RATES_COLUMN_MAP,
    CORE_PLAN_MAPPING,
    ICorePlanMetadata
} from 'src/constants/rateCards';
import { ROUTES } from 'src/constants/routes';
import { IMetadata } from 'src/types/IMetadata';
import {
    CORE_RATE_GRID_PLAN_COLUMNS,
    CoreRateGridPlanColumns,
    ICoreRatesPlanConfiguration,
    IFirstPartyRateGridRowData,
    IPlanNameToMetadataObj,
    IRateCardCoreRateGridRowData,
    IRateCardValues,
    IThirdPartyRateGridRowData
} from 'src/types/IRateCard';
import { Handlers, SaveFunc } from 'src/util/SaveManager/SaveManager';
import { maybeGet } from 'src/util/list-utils';

export interface RateCardSaveProps {
    /**
     * The ID of the contract whose data is being saved.
     */
    readonly contractId: number;

    /**
     * The alias of the user saving the data.
     */
    readonly user: string;

    /**
     * The data to save.
     */
    readonly values: IRateCardValues;

    /**
     * Plan metadata mapping.
     */
    readonly planMetadata: IPlanNameToMetadataObj;

    /**
     * General app-wide metadata.
     */
    readonly metadata: IMetadata;

    /**
     * First party free trial plans.
     */
    readonly firstPartyFreeTrialPlanList: Record<string, string | boolean | number>[];

    /**
     * Third party free trial  plans.
     */
    readonly thirdPartyFreeTrialPlanList: Record<string, string | boolean | number>[];
}

function getTerritoryId(territoryCode: string, metadata: IMetadata): number {
    const territory = Object.values(metadata.territoryMapping).find(
        (territory) => territory.territoryCode === territoryCode
    );

    return territory?.territoryId!;
}

function getContentAcquisitionEntityId(row: IRateCardCoreRateGridRowData, metadata: IMetadata): number {
    const caeCode = row[RateCardCoreRateGridColumnName.CAE];
    const cae = Object.values(metadata.CaeMapping!).find((mapping) => mapping.contentAcquisitionEntityCode === caeCode);

    return cae?.contentAcquisitionEntityID!;
}

function getAsdTieredRates(
    row: IRateCardCoreRateGridRowData,
    corePlanName: keyof IRateCardCoreRateGridRowData,
    props: RateCardSaveProps
): TieredRates | undefined {
    const asdRatchets = props.values.contractTerms?.asdRatchets;
    const ratchetModelName = asdRatchets?.inputDefinitions?.description;
    const ratchetModelId = maybeGet(props.metadata.ratchetModelMapping, ratchetModelName);
    const config = [
        {
            colName: RateCardCoreRateGridColumnName.ASDMonthlyTier2PSM,
            threshold: asdRatchets?.tier2Threshold
        },
        {
            colName: RateCardCoreRateGridColumnName.ASDMonthlyTier3PSM,
            threshold: asdRatchets?.tier3Threshold
        }
    ];
    const tiers = config.flatMap(({ colName, threshold }) => {
        const rate = row[colName];
        if (typeof rate === 'undefined' || rate === '') {
            return [];
        }
        return [
            RateTier.clone({
                rate: parseFloat(rate),
                threshold: typeof threshold === 'undefined' ? undefined : Number(threshold)
            })
        ];
    });

    if (tiers.length == 0) {
        return;
    }

    return TieredRates.clone({
        ratchetModelId: ratchetModelId,
        tiers: tiers
    });
}

function getTieredRates(
    row: IRateCardCoreRateGridRowData,
    corePlanName: keyof IRateCardCoreRateGridRowData,
    props: RateCardSaveProps
): TieredRates | undefined {
    if (corePlanName === RateCardCoreRateGridColumnName.ASDMonthlyTier1PSM) {
        return getAsdTieredRates(row, corePlanName, props);
    } else {
        return;
    }
}

function getRoyaltyModel(modelCode: string, modelType: RoyaltyModelType): RoyaltyModel {
    return modelType.royaltyModels!.find((rm) => rm.royaltyModelCode === modelCode)!;
}

function getCorePlanRoyaltyModelInfo(corePlan: string, planConfiguration: ICoreRatesPlanConfiguration) {
    const corePlanRoyaltyModelInfo =
        planConfiguration[CORE_PLAN_COLUMN_TO_CORE_RATES_COLUMN_MAP.get(corePlan) as RateCardCoreRateGridColumnName];
    return {
        royaltyModelCode: corePlanRoyaltyModelInfo!.corePlanModelCode,
        royaltyModelTypeCode: corePlanRoyaltyModelInfo!.royaltyModelTypeCode
    };
}

function getRoyaltyModelType(royaltyModelTypeCode: string, royaltyModelCode: string, metadata: IMetadata) {
    const modelType = metadata.royaltyModelTypes!.find(
        (modelType) => modelType.royaltyModelTypeCode === royaltyModelTypeCode
    )!;

    const model = getRoyaltyModel(royaltyModelCode, modelType);

    return {
        royaltyModelType: modelType,
        royaltyModel: model
    };
}

function getCoreRates(
    row: IRateCardCoreRateGridRowData,
    corePlanColumn: CoreRateGridPlanColumns,
    props: RateCardSaveProps
): CoreRate[] {
    if (typeof row[corePlanColumn] === 'undefined' || row[corePlanColumn] === '') {
        return [];
    }
    const paperPlan: ICorePlanMetadata = CORE_PLAN_MAPPING[corePlanColumn];
    const { royaltyModelType, royaltyModel } = getRoyaltyModelType(
        props.values.planConfiguration[corePlanColumn]!.royaltyModelTypeCode,
        props.values.planConfiguration[corePlanColumn]!.corePlanModelCode,
        props.metadata
    );
    const rates = [];
    rates.push(
        CoreRate.clone({
            territoryId: getTerritoryId(row[RateCardCoreRateGridColumnName.Territory]!, props.metadata),
            paperPlanId: parseInt(`${props.planMetadata[paperPlan.paperPlanName].paperPlanId!}`),
            royaltyModelId: royaltyModel!.royaltyModelId!,
            royaltyModelTypeId: royaltyModelType!.royaltyModelTypeId!,
            rate: parseFloat(row[corePlanColumn]!),
            currency: `${row[RateCardCoreRateGridColumnName.RatecardCurrency]}`,
            tieredRates: getTieredRates(row, corePlanColumn, props)
        })
    );

    const revShare = props.values.contractTerms?.revenue?.revSharePercent;
    const contractHasRevShare = typeof revShare === 'number' && revShare > 0;
    const modelHasRevShare = royaltyModelType?.royaltyModelTypeCode === RoyaltyModelTypesEnum.REV_PSM;
    if (contractHasRevShare && modelHasRevShare) {
        rates.push(
            CoreRate.clone({
                territoryId: getTerritoryId(row[RateCardCoreRateGridColumnName.Territory]!, props.metadata),
                paperPlanId: parseInt(`${props.planMetadata[paperPlan.paperPlanName].paperPlanId!}`),
                royaltyModelId: getRoyaltyModel(RoyaltyModelsEnum.REV_SHARE, royaltyModelType).royaltyModelId!,
                royaltyModelTypeId: royaltyModelType!.royaltyModelTypeId!,
                rate: revShare!,
                currency: `${row[RateCardCoreRateGridColumnName.RatecardCurrency]}`
            })
        );
    }

    return rates;
}

async function saveCoreRates(props: RateCardSaveProps): Promise<string> {
    let input: ModifyCoreRatesInput;
    try {
        const territoryTerms: TerritoryTermInput[] = props.values.coreRates.map((row) =>
            TerritoryTermInput.clone({
                territoryId: getTerritoryId(row[RateCardCoreRateGridColumnName.Territory]!, props.metadata),
                contentAcquisitionEntityId: getContentAcquisitionEntityId(row, props.metadata),
                settlementCurrency: row[RateCardCoreRateGridColumnName.SettlementCurrency]!
            })
        );
        const rates: CoreRate[] = props.values.coreRates.flatMap((row) =>
            CORE_RATE_GRID_PLAN_COLUMNS.flatMap((corePlanColumn) => getCoreRates(row, corePlanColumn, props))
        );
        input = ModifyCoreRatesInput.clone({
            contractId: props.contractId,
            requestedBy: props.user,
            territoryTerms: TerritoryTermInputs.fromJson(territoryTerms),
            rates: CoreRates.fromJson(rates),
            allowDeletion: true
        });
    } catch (e) {
        console.log(e);
        return Promise.resolve(`Caught error while building core rates payload: ${e}`);
    }
    return new Promise<string>((accept) => {
        contractConfigurationClient.modifyCoreRates(input).subscribe(
            (output: ModifyCoreRatesOutput) => accept(''),
            (error: UnknownCoralError) => {
                accept(`modifyCoreRates unexpected error ${JSON.stringify(error.receivedException)}`);
            }
        );
    });
}

function createFirstPartyDiscountRates(
    gridData: IFirstPartyRateGridRowData[],
    planMetadata: IPlanNameToMetadataObj
): DiscountRatesInputs {
    return DiscountRatesInputs.fromJson(
        gridData
            .filter((row) => row.multiplierName !== RateMultiplierDisplayName.CUSTOM)
            .map((row) => ({
                planId: planMetadata[row.paperPlanName].paperPlanId!,
                corePlanId: parseInt(row.corePlanId!),
                rateMultiplierTypeId: row.multiplierTypeId!
            }))
    );
}

function createFirstPartyCustomDiscountRates(
    gridData: IFirstPartyRateGridRowData[],
    planMetadata: IPlanNameToMetadataObj
): CustomDiscountRates {
    return CustomDiscountRates.fromJson(
        gridData
            .filter((row) => row.multiplierName === RateMultiplierDisplayName.CUSTOM)
            .map((row) => ({
                planId: planMetadata[row.paperPlanName].paperPlanId!,
                corePlanId: parseInt(row.corePlanId!),
                multiplier: parseFloat(row.multiplierValue)
            }))
    );
}

function revenueOverride(row: IThirdPartyRateGridRowData) {
    return row.revCalcType === CalcType.User ? parseFloat(row.serviceRev) : undefined;
}

function createThirdPartyDiscountRates(
    gridData: IThirdPartyRateGridRowData[],
    planMetadata: IPlanNameToMetadataObj,
    autoCalcRateMultiplierTypeId: number
): DiscountRatesInputs {
    return DiscountRatesInputs.fromJson(
        gridData
            .filter(
                (row) => row.rateCalcType !== CalcType.User && row.multiplierName !== RateMultiplierDisplayName.CUSTOM
            )
            .map((row) => ({
                planId: planMetadata[row.paperPlanName].paperPlanId!,
                corePlanId: parseInt(row.corePlanId!),
                rateMultiplierTypeId:
                    row.rateCalcType === CalcType.AutoCalc ? autoCalcRateMultiplierTypeId : row.multiplierTypeId!,
                revenueOverride: revenueOverride(row)
            }))
    );
}

function createCustomDiscountRates(
    gridData: IThirdPartyRateGridRowData[],
    planMetadata: IPlanNameToMetadataObj
): CustomDiscountRates {
    return CustomDiscountRates.fromJson(
        gridData
            .filter((row) => row.multiplierName === RateMultiplierDisplayName.CUSTOM)
            .map((row) => ({
                planId: planMetadata[row.paperPlanName].paperPlanId!,
                corePlanId: parseInt(row.corePlanId!),
                multiplier: parseFloat(row.multiplierValue)!,
                revenueOverride: revenueOverride(row)
            }))
    );
}

const saveFirstPartyRates: SaveFunc<RateCardSaveProps> = (props: RateCardSaveProps): Promise<string> => {
    const { contractId, values, planMetadata, user } = props;

    const input = SetDiscountRatesInput.clone({
        contractDiscountRates: [
            {
                contractId: contractId,
                discountRates: createFirstPartyDiscountRates(values.firstPartyRates, planMetadata),
                customDiscountRates: createFirstPartyCustomDiscountRates(values.firstPartyRates, planMetadata)
            } as ContractDiscountRatesInput
        ],
        changedBy: user
    });

    return new Promise<string>((accept) => {
        contractConfigurationClient.setDiscountRates(input).subscribe(
            (output: SetDiscountRatesOutput) => {
                accept('');
            },
            (error: UnknownCoralError) => {
                accept(`setDiscountRates unexpected error ${JSON.stringify(error.receivedException)}`);
            }
        );
    });
};

const saveContractTerms: SaveFunc<RateCardSaveProps> = (props: RateCardSaveProps) => {
    const { contractId, values, user } = props;
    let input: SetContractTermsInput;
    try {
        input = SetContractTermsInput.clone({
            contractId: contractId,
            contractTerms: createContractTerms(values.contractTerms),
            changedBy: user
        });
    } catch (e) {
        console.log(e);
        return Promise.resolve(`Caught error while building setContractTerms payload: ${e}`);
    }
    return new Promise<string>((accept) => {
        contractConfigurationClient.setContractTerms(input).subscribe(
            (output: SetContractTermsOutput) => accept(''),
            (error: UnknownCoralError) => {
                accept(`setContractTerms unexpected error ${JSON.stringify(error.receivedException)}`);
            }
        );
    });
};

function createContractTerms(contractTerms: AmuContractTerms): ContractTermsInput {
    return ContractTermsInput.clone({
        rateMultipliers: RateMultipliersInput.clone({
            daily: parseFloat(contractTerms.rateMultipliers!.daily!.toString()),
            weekly: parseFloat(contractTerms.rateMultipliers!.weekly!.toString()),
            annual: parseFloat(contractTerms.rateMultipliers!.annual!.toString()),
            priceDrop3m: parseFloat(contractTerms.rateMultipliers!.priceDrop3m!.toString()),
            priceDrop4m: parseFloat(contractTerms.rateMultipliers!.priceDrop4m!.toString()),
            priceDrop6m: parseFloat(contractTerms.rateMultipliers!.priceDrop6m!.toString()),
            winback: parseFloat(contractTerms.rateMultipliers!.winback!.toString()),
            extendedTrial2m: parseFloat(contractTerms.rateMultipliers!.extendedTrial2m!.toString()),
            extendedTrial3m: parseFloat(contractTerms.rateMultipliers!.extendedTrial3m!.toString()),
            extendedTrial4m: parseFloat(contractTerms.rateMultipliers!.extendedTrial4m!.toString()),
            extendedTrial5m: parseFloat(contractTerms.rateMultipliers!.extendedTrial5m!.toString()),
            extendedTrial6m: parseFloat(contractTerms.rateMultipliers!.extendedTrial6m!.toString()),
            asdExtendedTrial2m: parseFloat(contractTerms.rateMultipliers!.asdExtendedTrial2m!.toString()),
            asdExtendedTrial3m: parseFloat(contractTerms.rateMultipliers!.asdExtendedTrial3m!.toString())
        }),
        revShare: Revenue.clone({
            revSharePercent: parseFloat(contractTerms.revenue!.revSharePercent!.toString()),
            includeBadDebt: contractTerms.revenue!.includeBadDebt,
            includeChargebacks: contractTerms.revenue!.includeChargebacks,
            vpirBundleRevenueDeduction: isUndefined(contractTerms.revenue!.vpirBundleRevenueDeduction)
                ? undefined
                : VpirBundleRevenueDeduction.fromJson(
                      Object.fromEntries(
                          Object.entries(contractTerms.revenue!.vpirBundleRevenueDeduction).map(([key, value]) => [
                              key,
                              parseFloat(value as unknown as string)
                          ])
                      )
                  )
        }),
        asdRatchets: ASDRatchets.clone({
            inputDefinitions: contractTerms.asdRatchets!.inputDefinitions,
            rollingMonthlyAverage: parseInt(contractTerms.asdRatchets!.rollingMonthlyAverage!.toString()),
            tier2Threshold: contractTerms.asdRatchets?.tier2Threshold
                ? parseFloat(contractTerms.asdRatchets!.tier2Threshold!.toString())
                : undefined,
            tier3Threshold: contractTerms.asdRatchets?.tier3Threshold
                ? parseFloat(contractTerms.asdRatchets!.tier3Threshold!.toString())
                : undefined,
            thresholdOperator:
                contractTerms.asdRatchets?.thresholdOperator ?? ThresholdOperatorEnum.GREATER_THAN_OR_EQUAL_TO
        }),
        thirdPartyDeductionCaps: ThirdPartyDeductionCaps.clone({
            globalDeductionCap: parseFloat(contractTerms.thirdPartyDeductionCaps!.globalDeductionCap!.toString()),
            territoryOverrides: TerritoryOverrides.fromJson(
                contractTerms.thirdPartyDeductionCaps!.territoryOverrides
                    ? contractTerms.thirdPartyDeductionCaps!.territoryOverrides
                    : []
            ),
            appStore: parseFloat(contractTerms.thirdPartyDeductionCaps!.appStore!.toString()),
            carrierBilling: parseFloat(contractTerms.thirdPartyDeductionCaps!.carrierBilling!.toString()),
            appStoreUsesRetailPrice: contractTerms.thirdPartyDeductionCaps!.appStoreUsesRetailPrice ?? false
        }),
        flatDiscount: !isUndefined(contractTerms.flatDiscount),
        umgPerformanceDiscount: !isEmpty(contractTerms.performanceDiscounts),
        studentTrueUpThresholdValue: contractTerms.additionalSettings!.studentTrueUpThresholdValue
            ? parseFloat(contractTerms.additionalSettings!.studentTrueUpThresholdValue)
            : undefined,
        subscriberVolumeBasedDiscount: !isUndefined(contractTerms.subscriberVolumeDiscount?.threshold),
        vendorShareCalculationMapping: VendorShareCalculationTypeMappingList.fromJson(
            contractTerms.vendorShareCalculationMapping ?? []
        ),
        playCountAggregationTypeMapping: PlayCountAggregationTypeMappingList.fromJson(
            contractTerms.playCountAggregationTypeMapping ?? []
        ),
        prsDeductionCap:
            contractTerms.prsDeductionCap &&
            PrsDeductionCap.clone({
                type: contractTerms.prsDeductionCap.type,
                value: Number(contractTerms.prsDeductionCap.value)
            }),
        streamingTaxDeductionCaps: StreamingTaxDeductionCaps.fromJson(contractTerms.streamingTaxDeductionCaps ?? [])
    });
}

const saveThirdPartyRates: SaveFunc<RateCardSaveProps> = (props: RateCardSaveProps): Promise<string> => {
    const { contractId, values, planMetadata, user } = props;

    const discountRatesInput = SetDiscountRatesInput.clone({
        contractDiscountRates: [
            {
                contractId: contractId,
                discountRates: createThirdPartyDiscountRates(
                    values.thirdPartyData.thirdPartyRates,
                    planMetadata,
                    values.thirdPartyData.autoCalcRateMultiplierTypeId
                ),
                customDiscountRates: createCustomDiscountRates(values.thirdPartyData.thirdPartyRates, planMetadata)
            } as ContractDiscountRatesInput
        ],
        changedBy: user
    });

    const territoryTerms: TerritoryTermInput[] = props.values.coreRates.map((row) =>
        TerritoryTermInput.clone({
            territoryId: getTerritoryId(row[RateCardCoreRateGridColumnName.Territory]!, props.metadata),
            contentAcquisitionEntityId: getContentAcquisitionEntityId(row, props.metadata),
            settlementCurrency: row[RateCardCoreRateGridColumnName.SettlementCurrency]!
        })
    );

    const rates: CoreRate[] = props.values.thirdPartyData.thirdPartyRates
        .filter((row) => row.rateCalcType === CalcType.User)
        .flatMap((row) => {
            let royaltyModelInfo = {
                royaltyModelTypeCode: RoyaltyModelsEnum.CPP as string,
                royaltyModelCode: RoyaltyModelsEnum.CPP as string
            };
            if (row.rateType === RoyaltyModelsEnum.PSM) {
                royaltyModelInfo = getCorePlanRoyaltyModelInfo(row.corePlan, props.values.planConfiguration);
            }

            const { royaltyModelType, royaltyModel } = getRoyaltyModelType(
                royaltyModelInfo.royaltyModelTypeCode,
                royaltyModelInfo.royaltyModelCode,
                props.metadata
            );

            const rates: CoreRate[] = [];

            rates.push(
                CoreRate.clone({
                    territoryId: getTerritoryId(row.territory, props.metadata),
                    paperPlanId: parseInt(`${props.planMetadata[row.paperPlanName].paperPlanId!}`),
                    royaltyModelId: royaltyModel.royaltyModelId!,
                    royaltyModelTypeId: royaltyModelType.royaltyModelTypeId!,
                    rate: parseFloat(row.finalRate),
                    currency: `${row.rateCardCurrency}`,
                    tieredRates: undefined,
                    revenueOverride: row.revCalcType === CalcType.User ? parseFloat(row.serviceRev) : undefined
                })
            );

            if (
                royaltyModel.royaltyModelCode === RoyaltyModelsEnum.PSM &&
                royaltyModelType.royaltyModelTypeCode == RoyaltyModelTypesEnum.REV_PSM
            ) {
                rates.push(
                    CoreRate.clone({
                        territoryId: getTerritoryId(row.territory, props.metadata),
                        paperPlanId: parseInt(`${props.planMetadata[row.paperPlanName].paperPlanId!}`),
                        royaltyModelId: getRoyaltyModel(RoyaltyModelsEnum.REV_SHARE, royaltyModelType).royaltyModelId!,
                        royaltyModelTypeId: royaltyModelType.royaltyModelTypeId!,
                        rate: Number(props.values.contractTerms!.revenue!.revSharePercent!),
                        currency: `${row.rateCardCurrency}`,
                        tieredRates: undefined,
                        revenueOverride: row.revCalcType === CalcType.User ? parseFloat(row.serviceRev) : undefined
                    })
                );
            }
            return rates;
        });

    // Save rates for Free Trial plans as part of saveThirdPartyRates before the review step
    const freeTrialRates = createFreeTrialCoreRates(props);

    const coreRatesInput = ModifyCoreRatesInput.clone({
        contractId: props.contractId,
        requestedBy: props.user,
        territoryTerms: TerritoryTermInputs.fromJson(territoryTerms),
        rates: CoreRates.fromJson([...rates, ...freeTrialRates])
    });

    return new Promise<string>((accept) => {
        Promise.all([
            contractConfigurationClient.setDiscountRates(discountRatesInput).toPromise(),
            contractConfigurationClient.modifyCoreRates(coreRatesInput).toPromise()
        ])
            .then((response) => {
                accept('');
            })
            .catch((error) => {
                accept(`The third party rates save have unexpected error ${JSON.stringify(error.receivedException)}`);
            });
    });
};

const createFreeTrialCoreRates = (props: RateCardSaveProps) => {
    const coreRatesTerritories = props.values.coreRates.map(
        (coreRate) => coreRate[RateCardCoreRateGridColumnName.Territory]
    );

    const firstPartyFreeTrialRates = props.firstPartyFreeTrialPlanList.flatMap((firstPartyPlan) => {
        const corePlanRoyaltyModelInfo = getCorePlanRoyaltyModelInfo(
            firstPartyPlan.corePlan as string,
            props.values.planConfiguration
        );
        const { royaltyModelType, royaltyModel } = getRoyaltyModelType(
            corePlanRoyaltyModelInfo!.royaltyModelTypeCode,
            corePlanRoyaltyModelInfo!.royaltyModelCode,
            props.metadata
        );

        return props.values.coreRates.flatMap((coreRate) => {
            const corePlanColumnName = CORE_PLAN_COLUMN_TO_CORE_RATES_COLUMN_MAP.get(firstPartyPlan.corePlan as string);
            const corePlanPsm = coreRate[corePlanColumnName!];

            if (corePlanPsm === undefined || corePlanPsm === '') {
                return [];
            } else {
                return royaltyModelType!.royaltyModels!.map((royaltyModelObj) => {
                    return CoreRate.clone({
                        territoryId: getTerritoryId(
                            coreRate[RateCardCoreRateGridColumnName.Territory]!,
                            props.metadata
                        ),
                        paperPlanId: firstPartyPlan.paperPlanId as number,
                        royaltyModelId: royaltyModelObj.royaltyModelId!,
                        royaltyModelTypeId: royaltyModelType.royaltyModelTypeId!,
                        rate:
                            royaltyModelObj.royaltyModelCode === RoyaltyModelsEnum.REV_SHARE
                                ? Number(props.values.contractTerms?.revenue?.revSharePercent)
                                : 0,
                        revenueOverride: undefined,
                        currency: coreRate[RateCardCoreRateGridColumnName.RatecardCurrency]!
                    });
                });
            }
        });
    });

    const thirdPartyFreeTrialRates = props.thirdPartyFreeTrialPlanList
        .filter((thirdPartyPlan) => coreRatesTerritories.includes(thirdPartyPlan.territory as string))
        .flatMap((thirdPartyPlan) => {
            const coreRate = props.values.coreRates.find(
                (coreRate) => coreRate.Territory === thirdPartyPlan.territory
            )!;

            const corePlanRoyaltyModelInfo = getCorePlanRoyaltyModelInfo(
                thirdPartyPlan.corePlan as string,
                props.values.planConfiguration
            );
            const { royaltyModelType, royaltyModel } = getRoyaltyModelType(
                corePlanRoyaltyModelInfo!.royaltyModelTypeCode,
                corePlanRoyaltyModelInfo!.royaltyModelCode,
                props.metadata
            );

            const corePlanColumnName = CORE_PLAN_COLUMN_TO_CORE_RATES_COLUMN_MAP.get(thirdPartyPlan.corePlan as string);
            const corePlanPsm = coreRate[corePlanColumnName!];

            if (corePlanPsm === undefined || corePlanPsm === '') {
                return [];
            } else {
                return royaltyModelType!.royaltyModels!.map((royaltyModelObj) => {
                    return CoreRate.clone({
                        territoryId: getTerritoryId(
                            coreRate[RateCardCoreRateGridColumnName.Territory] as string,
                            props.metadata
                        ),
                        paperPlanId: thirdPartyPlan.paperPlanId as number,
                        royaltyModelId: royaltyModelObj.royaltyModelId!,
                        royaltyModelTypeId: royaltyModelType.royaltyModelTypeId!,
                        rate:
                            royaltyModelObj.royaltyModelCode === RoyaltyModelsEnum.REV_SHARE
                                ? Number(props.values.contractTerms?.revenue?.revSharePercent)
                                : 0,
                        revenueOverride: 0,
                        currency: coreRate[RateCardCoreRateGridColumnName.RatecardCurrency]!
                    });
                });
            }
        });

    return [...firstPartyFreeTrialRates, ...thirdPartyFreeTrialRates];
};

const publishContract: SaveFunc<RateCardSaveProps> = (props: RateCardSaveProps): Promise<string> => {
    const input = PublishContractInput.clone({
        contractId: props.contractId,
        changedBy: props.user
    });
    return new Promise<string>((accept) => {
        contractConfigurationClient.publishContract(input).subscribe(
            (output: PublishContractOutput) => accept(''),
            (error: UnknownCoralError) => {
                accept(
                    `Publishing contract encountered an unexpected error ${JSON.stringify(error.receivedException)}`
                );
            }
        );
    });
};

export const handlers: Handlers<RateCardSaveProps> = {
    [ROUTES.RATE_CARDS.STEP.CORE_RATES]: saveCoreRates,
    [ROUTES.RATE_CARDS.STEP.CONTRACT_TERMS]: saveContractTerms,
    [ROUTES.RATE_CARDS.STEP.FIRST_PARTY_DISCOUNTED_RATES]: saveFirstPartyRates,
    [ROUTES.RATE_CARDS.STEP.THIRD_PARTY_DISCOUNTED_RATES]: saveThirdPartyRates,
    [ROUTES.RATE_CARDS.STEP.REVIEW]: publishContract
};

export const fullStepList = [
    ROUTES.RATE_CARDS.STEP.CORE_RATES,
    ROUTES.RATE_CARDS.STEP.CONTRACT_TERMS,
    ROUTES.RATE_CARDS.STEP.FIRST_PARTY_DISCOUNTED_RATES,
    ROUTES.RATE_CARDS.STEP.THIRD_PARTY_DISCOUNTED_RATES
];
